import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { useParams, useSearchParams } from "react-router";
import { api } from "../../data";
import { useSession } from "../../app/session";
import { PublicLayout } from "../../components/shell/WebLayout";
import { EMAIL, buildView, tokenLooksValid, type InsightsLang, type ShareOutcome } from "./insights-model";
import { openShare, openShareAsTeam, type TeamOutcome } from "./insights-data";
import { kitLangVorab } from "./kit-model";
import { linkBeendet } from "./kit-data";
import { InsightsAnsicht } from "./InsightsAnsicht";
import { useIgMediaResolver } from "./insights-media";

/**
 * Insights-Freigabe für Marken unter /i/:token (13_oeffentlich/insights.html; OE-030, OE-031).
 * Vor der Prüfung steht nichts Inhaltliches im Dokument. Die Marke weist sich mit der Adresse aus, an die der Link ging;
 * nach fünf Fehlversuchen sperrt die Datenbank die Freigabe. Zurückgezogen, abgelaufen, gesperrt, ungültig: je ein
 * eigener Zustand ohne Daten. Die Freigabe wird nur auf Eingabe geöffnet (jeder Aufruf zählt), nie in einer Abfrage.
 * Ist ein Mitarbeiter der Agentur angemeldet, zeigt die Seite die Freigabe direkt als Teamansicht (ig_share_open_team):
 * ohne Mail-Prüfung und ohne dass der Aufruf bei der Marke mitzählt.
 * Sprache (SICHT-OEFFENTLICH-BETRIEB-04): Deutsch oder Englisch umschaltbar; vorab nach der Sprache des Links (?l=en setzt die Mail
 * an eine englischsprachige Marke, 0081), sonst nach der Browsersprache. Die Ansicht wird in der gewählten Sprache gebaut.
 */
export function InsightsFreigabe() {
  const { token = "" } = useParams();
  return <Freigabe key={token} token={token} />;
}

function Freigabe({ token }: { token: string }) {
  const [outcome, setOutcome] = useState<ShareOutcome | null>(() => (tokenLooksValid(token) ? null : { kind: "invalid" }));
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [params] = useSearchParams();
  const [lang, setLang] = useState<InsightsLang>(() => kitLangVorab(`?${params.toString()}`, typeof navigator !== "undefined" ? navigator.language : undefined));
  const t = (de: string, en: string) => (lang === "en" ? en : de);
  const pending = useRef(false);
  const input = useRef<HTMLInputElement>(null);
  const { session, ready } = useSession();
  const [team, setTeam] = useState<TeamOutcome | null>(null);
  const userId = session?.userId ?? null;
  // 0425: Vertrag der Agentur beendet: sofort der neutrale Zustand, ohne die Mailadresse abzufragen
  useEffect(() => {
    if (!tokenLooksValid(token)) return;
    let alive = true;
    void linkBeendet(api, "share", token).then((ende) => { if (alive && ende) setOutcome({ kind: "ended" }); });
    return () => { alive = false; };
  }, [token]);
  useEffect(() => {
    if (!ready || !userId || !tokenLooksValid(token)) {
      setTeam(null);
      return;
    }
    let alive = true;
    void openShareAsTeam(api, token).then((r) => alive && setTeam(r));
    return () => {
      alive = false;
    };
  }, [ready, userId, token]);
  // Die geöffnete Freigabe in der gewählten Sprache (Umschalten ohne erneuten Aufruf, der bei der Marke mitzählen würde)
  const openView = useMemo(() => (outcome?.kind === "open" ? (outcome.raw ? buildView(outcome.raw, outcome.view.email, lang) : outcome.view) : null), [outcome, lang]);
  // 0429: Teamansicht mit der Anmeldung: Bilder als signierte Adressen über die Storage-Policy der Agentur
  const teamMedia = useIgMediaResolver(team?.kind === "team" ? team.raw : null);
  const teamView = useMemo(() => (team?.kind === "team" ? (team.raw ? buildView(team.raw, team.recipient ?? (lang === "en" ? "the brand" : "die Marke"), lang, teamMedia) : team.view) : null), [team, lang, teamMedia]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (pending.current) return;
    const value = email.trim();
    if (!EMAIL.test(value)) {
      setFieldError(t("Bitte geben Sie eine gültige E-Mail-Adresse ein.", "Please enter a valid email address."));
      input.current?.focus();
      return;
    }
    setFieldError(null);
    pending.current = true;
    setBusy(true);
    try {
      const next = await openShare(api, token, value);
      setOutcome(next);
      if (next.kind === "wrong" || next.kind === "failed") input.current?.focus();
    } finally {
      pending.current = false;
      setBusy(false);
    }
  }

  const sprache = (
    <div className="row" role="group" aria-label={t("Sprache", "Language")}>
      <button type="button" lang="de" aria-pressed={lang === "de"} onClick={() => setLang("de")}>Deutsch</button>
      <button type="button" lang="en" aria-pressed={lang === "en"} onClick={() => setLang("en")}>English</button>
    </div>
  );

  let content: React.ReactNode;
  if (team?.kind === "team" && teamView) {
    content = (
      <>
        <div className="notice" role="status">
          Teamansicht: So sieht {team.recipient ?? "die Marke"} diese Freigabe. Dein Aufruf zählt nicht als Aufruf der Marke.
          {team.locked && " Die Marke hat den Link durch falsche E-Mail-Adressen gesperrt; entsperren kannst du ihn in der Freigabeliste beim Creator."}
        </div>
        {sprache}
        <InsightsAnsicht view={teamView} />
      </>
    );
  } else if (team?.kind === "revoked" && !outcome) content = <Zustand lang={lang} title="Diese Freigabe wurde zurückgezogen." text="Die Marke sieht an dieser Stelle nur diesen Hinweis. Eine neue Freigabe legst du beim Creator an." />;
  else if (team?.kind === "expired" && !outcome) content = <Zustand lang={lang} title="Dieser Link ist abgelaufen." text="Die Marke sieht an dieser Stelle nur diesen Hinweis. Eine neue Freigabe legst du beim Creator an." />;
  else if (outcome?.kind === "open" && openView) content = <>{sprache}<InsightsAnsicht view={openView} /></>;
  // 0425 (S-7): Vertrag der Agentur beendet: neutral, ohne Agenturnamen
  else if (outcome?.kind === "ended") content = <Zustand lang={lang} title={t("Dieser Link ist nicht mehr verfügbar.", "This link is no longer available.")} text={t("Bitte wenden Sie sich an Ihre Kontaktperson.", "Please contact your contact person.")} />;
  else if (outcome?.kind === "revoked") content = <Zustand lang={lang} title={t("Diese Freigabe wurde zurückgezogen.", "This share has been withdrawn.")} text={t("Die Agentur hat den Zugang beendet. Wenn Sie die Kennzahlen weiter brauchen, wenden Sie sich bitte an Ihre Kontaktperson.", "The agency has ended access. If you still need the metrics, please contact your contact person.")} />;
  else if (outcome?.kind === "expired") content = <Zustand lang={lang} title={t("Dieser Link ist abgelaufen.", "This link has expired.")} text={t("Bitte fordern Sie bei Ihrer Kontaktperson einen neuen Link an.", "Please ask your contact person for a new link.")} />;
  else if (outcome?.kind === "locked") content = <Zustand lang={lang} title={t("Diese Freigabe ist gesperrt.", "This share is locked.")} text={t("Nach fünf Versuchen mit einer falschen E-Mail-Adresse ist der Link gesperrt. Bitte fordern Sie bei Ihrer Kontaktperson einen neuen Link an.", "After five attempts with a wrong email address the link is locked. Please ask your contact person for a new link.")} />;
  else if (outcome?.kind === "invalid") content = <Zustand lang={lang} title={t("Dieser Link ist ungültig.", "This link is invalid.")} text={t("Bitte öffnen Sie den Link direkt aus der E-Mail. Vielleicht wurde er unvollständig kopiert.", "Please open the link directly from the email. It may have been copied incompletely.")} />;
  else {
    const notice = fieldError
      ?? (outcome?.kind === "wrong"
        ? t(`Diese E-Mail-Adresse passt nicht zur Freigabe. Bitte geben Sie genau die Adresse ein, an die der Link geschickt wurde. Noch ${outcome.remaining} ${outcome.remaining === 1 ? "Versuch" : "Versuche"}, danach wird der Link gesperrt.`,
          `This email address does not match the share. Please enter exactly the address the link was sent to. ${outcome.remaining} ${outcome.remaining === 1 ? "attempt" : "attempts"} left before the link is locked.`)
        : outcome?.kind === "failed"
          ? t("Die Prüfung ist gerade nicht möglich. Ihre Eingabe bleibt erhalten. Bitte versuchen Sie es in einem Moment erneut.", "The check is not possible right now. Your input is kept. Please try again in a moment.")
          : null);
    content = (
      <>
        <title>Insights · CreatorOffice</title>
        <form className="centerform panel stack" onSubmit={(e) => void submit(e)} noValidate aria-busy={busy}>
          {sprache}
          <h1>{t("Creator-Insights", "Creator insights")}</h1>
          <p>{t("Diese Übersicht wurde für Sie freigegeben. Bitte bestätigen Sie mit der E-Mail-Adresse, an die der Link geschickt wurde.",
            "This overview has been shared with you. Please confirm with the email address the link was sent to.")}</p>
          <label>
            {t("E-Mail-Adresse", "Email address")}
            <input
              ref={input} id="insights-mail" name="email" type="email" inputMode="email" enterKeyHint="go" autoComplete="email" autoCapitalize="off"
              spellCheck={false} required value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t("name@firma.de", "name@company.com")}
              aria-invalid={notice ? true : undefined} aria-describedby={notice ? "insights-hinweis" : undefined}
            />
          </label>
          {notice && <p id="insights-hinweis" className="tk-error" role="alert">{notice}</p>}
          <button className="primary" type="submit" disabled={busy} aria-busy={busy}>{busy ? t("Wird geprüft …", "Checking …") : t("Insights ansehen", "View insights")}</button>
          <small>{t("Der Zugang ist personengebunden. Aufrufe werden protokolliert.", "Access is personal. Views are logged.")}</small>
        </form>
        {api.mode === "demo" && <p className="demo-note">{t("Demo · Alle Daten sind erfunden.", "Demo · All data is fictitious.")}</p>}
      </>
    );
  }

  // Nach bestandener Prüfung (bzw. in der Teamansicht) ist die Agentur der Absender im Kopf und im Fuß; davor bleibt er neutral
  // (SICHT-OEFFENTLICH-BETRIEB-28)
  const agency = (team?.kind === "team" ? teamView?.agency : outcome?.kind === "open" ? openView?.agency : null) ?? null;
  return (
    <PublicLayout lang={lang} sender={agency ? { name: agency } : null}>
      {/* Persönlicher Link: kein Suchindex; der Schlüssel in der Adresse wandert nie als Referrer mit (React hebt beides in den Kopf) */}
      <meta name="robots" content="noindex, nofollow, noarchive" />
      <meta name="referrer" content="no-referrer" />
      <main id="main" className="auth-shell stack">{content}</main>
    </PublicLayout>
  );
}

function Zustand({ title, text, lang }: { title: string; text: string; lang: InsightsLang }) {
  return (
    <>
      <title>{`${title} · CreatorOffice`}</title>
      <section className="centerform panel stack" role="status">
        <h1>{title}</h1>
        <p>{text}</p>
        <p>{lang === "en" ? "No profile data is shown." : "Es werden keine Profildaten angezeigt."}</p>
      </section>
    </>
  );
}
